import React, { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import appBranding from '@happylife-a/branding';
import { Box, Center, Spinner } from '@chakra-ui/react';
import { theme } from '../../theme';
import useLoadYandexMaps from './useLoadYandexMaps';

export default function YandexMap({
  address,
  markers = [],
  height = '500px',
  enableClick = true,
  setSelectedAddress,
}) {
  const brandEnv = appBranding.configs.environments;
  const coordinatesFields = appBranding.fields.brand.CountryParam.Map.InitialCenter;

  const mapRef = useRef(null);
  const placemarkRef = useRef(null);
  const [isMapReady, setIsMapReady] = useState(false);

  const initialCenter = [
    brandEnv.getField(coordinatesFields.Latitude),
    brandEnv.getField(coordinatesFields.Longitude),
  ];

  if (initialCenter.some((value) => value === undefined)) {
    return (
      <Center h="full">
        <Spinner />
      </Center>
    );
  }

  const markerIcon = useMemo(
    () =>
      'data:image/svg+xml;charset=UTF-8,' +
      encodeURIComponent(
        `
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="20" r="20" fill="${theme.colors.primary[500]}" />
            <circle cx="20.001" cy="20.0001" r="9.47368" fill="white" />
          </svg>
        `
      ),
    []
  );

  const createPlacemark = useCallback(
    (coordinates, balloonContent) => {
      return new window.ymaps.Placemark(
        coordinates,
        { balloonContent: balloonContent },
        {
          iconLayout: 'default#image',
          iconImageHref: markerIcon,
          iconImageSize: [30, 42],
          iconImageOffset: [-15, -42],
        }
      );
    },
    [markerIcon]
  );

  const reverseGeocode = useCallback(async (coords) => {
    try {
      const res = await window.ymaps.geocode(coords);

      if (res.geoObjects.getLength() === 0) {
        throw new Error('No geocoding results');
      }

      const firstGeoObject = res.geoObjects.get(0);
      const clickedAddress = firstGeoObject.getAddressLine();

      if (!clickedAddress || clickedAddress.trim() === '') {
        throw new Error('No valid address found');
      }

      const city =
        firstGeoObject.getLocalities().length > 0 ? firstGeoObject.getLocalities()[0] : '';

      const country = firstGeoObject.getCountry();
      const region =
        firstGeoObject.getAdministrativeAreas().length > 0
          ? firstGeoObject.getAdministrativeAreas()[0]
          : '';

      return {
        address: clickedAddress,
        city,
        country,
        region,
        latitude: coords[0],
        longitude: coords[1],
        zipCode: '',
        accuracy: 1,
      };
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      return {
        address: `Lat: ${coords[0].toFixed(6)}, Lng: ${coords[1].toFixed(6)}`,
        city: '',
        country: '',
        region: '',
        latitude: coords[0],
        longitude: coords[1],
        zipCode: '',
        accuracy: 0,
      };
    }
  }, []);

  useLoadYandexMaps(() => {
    if (window.ymaps && !mapRef.current) {
      mapRef.current = new window.ymaps.Map('map', {
        center: initialCenter,
        zoom: 15,
        controls: ['zoomControl', 'geolocationControl', 'typeSelector'],
        type: 'yandex#map',
        autoFitToViewport: 'always',
        geoObjectOptions: {
          preset: 'islands#blueDotIcon',
          openBalloonOnClick: false,
          openHintOnHover: false,
        },
      });

      setIsMapReady(true);

      if (enableClick) {
        mapRef.current.events.add('click', async (e) => {
          const coords = e.get('coords');
          const locationData = await reverseGeocode(coords);
          setSelectedAddress(locationData);
        });
      }
    }
  }, [reverseGeocode]);

  useEffect(() => {
    if (isMapReady && address && mapRef.current) {
      const newCoordinates = [address.latitude, address.longitude];

      if (placemarkRef.current) {
        mapRef.current.geoObjects.remove(placemarkRef.current);
      }

      placemarkRef.current = createPlacemark(newCoordinates, address?.address);
      mapRef.current.geoObjects.add(placemarkRef.current);

      const currentCenter = mapRef.current.getCenter();
      const isSameLocation =
        Math.abs(currentCenter[0] - newCoordinates[0]) < 0.0001 &&
        Math.abs(currentCenter[1] - newCoordinates[1]) < 0.0001;

      if (!isSameLocation) {
        const currentZoom = mapRef.current.getZoom();
        mapRef.current.setCenter(newCoordinates, currentZoom, {
          checkZoomRange: true,
          duration: 500,
          timingFunction: 'ease-in-out',
        });
      }
    }
  }, [address, createPlacemark, isMapReady]);

  useEffect(() => {
    if (isMapReady && markers.length > 0 && mapRef.current) {
      mapRef.current.geoObjects.removeAll();
      const bounds = markers.map((marker) => {
        const coordinates = [marker.coordinates.latitude, marker.coordinates.longitude];

        const placemark = createPlacemark(coordinates, marker.name);
        mapRef.current.geoObjects.add(placemark);

        return coordinates;
      });

      if (bounds.length > 0) {
        const mapBounds = window.ymaps.util.bounds.fromPoints(bounds);
        mapRef.current.setBounds(mapBounds, {
          checkZoomRange: true,
          duration: 500,
          timingFunction: 'ease-in-out',
        });

        mapRef.current.events.once('boundschange', () => {
          mapRef.current.setZoom(18);
        });
      }
    }
  }, [markers, createPlacemark, isMapReady]);

  return <Box id="map" style={{ width: '100%', height: height }} />;
}
